/**
 * Which chart series start DEACTIVATED, expressed once for every widget that
 * draws candidacy stages.
 *
 * Jared, 2026-08-25: *"I would deactivate all of these things. Client, contact,
 * reference, source, jury member, carve out, stakeholder, place… Or just default
 * to having them deactivated."* then, one sentence later, widening it himself:
 * *"Same defaults as above to deactivate these in the chart and each chart."*
 *
 * THE RULE IS DERIVED, NOT ENUMERATED, and that is the whole reason this file
 * exists rather than a `const HIDDEN = [...]` next to each chart. Thrive's stage
 * vocabulary is tenant-configurable — `GET /api/v2/candidacy_stages` returned 24
 * definitions on 2026-05-06 and a 25th is a normal event — so a hand-kept list of
 * "the ones to hide" goes stale silently the first time a stage is added. What
 * does not go stale is the LADDER: eight rungs, ours rather than Thrive's,
 * declared once in the backend as `FUNNEL_STAGE_ORDER`
 * (`packages/python-shared/nu_assist_shared/domain/search_report.py:80`) and
 * mirrored in `components/stages/ladder.ts`. Everything off the ladder starts hidden; a stage Thrive adds
 * tomorrow is off the ladder and therefore off by default, with no edit here.
 *
 * The list Jared dictated carried three errors, recorded so nobody re-derives it
 * from the transcript and "fixes" this file back:
 *
 *   1. "Client, contact" is ONE stage — `Client Contact` (id 23) — not two.
 *   2. He named `Source` and `Placed`, and both are ladder rungs. Turning them
 *      off leaves the chart without the start or the end of the pipeline, and
 *      Source carries real volume (150 entries on search 3378). They STAY ON.
 *      SUPERSEDED FOR `Source` ON 2026-08-27 — see {@link STAGES_NOT_CANDIDATES}.
 *      `Placed` is untouched and still starts on.
 *   3. He omitted `On Hold` (id 26), which is a hold and follows the rule, so it
 *      starts off.
 *
 * `Rejected` (8) and `Unresponsive` (20) are off by Jose's criterion, not
 * Jared's — the transcript is silent on both. "Default off; activate it if you
 * want the data", which also fixes a scale problem the call never reached:
 * Rejected=483 against Reference=3 on search 3378 flattens every other line on
 * the same axis.
 *
 * WHY THE OUTREACH CHART IS UNAFFECTED. `OutreachLines` plots one series per
 * CONTACT METHOD (Email, LinkedIn, Phone…), not per candidacy stage. Feeding
 * method names through {@link stageHiddenByDefault} would report every one of
 * them as off-ladder and open that chart with nothing drawn, so the outreach
 * chart deliberately does not consume this module. The rule still reaches "each
 * chart" in Jared's sense: every chart that draws stages applies it, and the one
 * that draws methods has no stage to deactivate.
 *
 * Consumers: the two `StageLines` on the Search Progress tab (sub-task 03) and
 * the Search Dashboard's stage widgets (sub-task 04). One rule, imported twice —
 * the failure mode this replaces is two catalogues that agree today.
 */

import type { SearchReportBucket, SearchReportFunnelStep } from "@nu-assist/contracts";

import { isLadderStage } from "../../../../components/stages/ladder";

/**
 * Stages held by people who are NOT candidates on the search.
 *
 * Jared, 2026-08-27, pointing at the Search Dashboard: *"Get rid of jury member.
 * Just get rid of source and jury member. The two that should not be there…
 * some of these are candidates in the search, which is what we care about, other
 * ones are not candidates. The jury member is someone who works for the client;
 * the source is someone who may give us some ideas, but we don't need them for
 * this purpose."*
 *
 * THIS IS AN EXCEPTION TO THE LADDER RULE, AND DELIBERATELY SO. `Source` is rung
 * 1 of {@link LADDER_STAGES}, so every rule in this file would otherwise start it
 * ACTIVE — which is exactly what #729 cl.3 decided on 2026-08-25, with an
 * argument that was good at the time: Source is the start of the pipeline and
 * carries the most volume of any stage (150 entries on search 3378). Jared
 * overrode it four days later on a different ground. His is about the DOMAIN —
 * these rows are not candidates — and a domain argument outranks a structural
 * one, so the exception stands and the ladder is left alone.
 *
 * DO NOT "FIX" THIS BY DELETING IT. A reader who checks `Source` against
 * `LADDER_STAGES`, finds it there, and concludes the exception is a bug will
 * silently restore the 08-25 behaviour. That is the failure this comment exists
 * to prevent.
 *
 * THE CONSEQUENCE IS ACCEPTED KNOWINGLY: Search Progress now opens without its
 * highest-volume series. One click brings it back — the toggle still exists, and
 * the data was never removed from the payload.
 *
 * `Jury Member` is off-ladder and the ladder rule already hid it. It is named
 * here anyway, because the Dashboard widgets have no toggle and need to know
 * which stages to drop outright, and because Jared named the two together.
 *
 * WHERE THIS IS APPLIED, and where it is NOT:
 *   - Search Progress stage charts — start DEACTIVATED, via
 *     {@link stageHiddenByDefault}. Reversible by the reader.
 *   - Search Dashboard donut + funnel — dropped from the series entirely, at
 *     render. Those widgets have no toggle, so hiding means removing.
 *   - The recruiting ladder itself — UNTOUCHED. `FUNNEL_STAGE_ORDER` also feeds
 *     `highest_stage`, `_ladder_key` and the ladder sort of `CandidatesTable`;
 *     nobody asked for the candidate table to re-sort.
 *   - The stored report — UNTOUCHED. Excluding at render keeps the payload
 *     complete, so this is one edit to reverse rather than a data migration.
 */
export const STAGES_NOT_CANDIDATES: readonly string[] = ["Source", "Jury Member"] as const;

const NOT_CANDIDATES: ReadonlySet<string> = new Set(STAGES_NOT_CANDIDATES);

/** True for a stage whose holders are not candidates on the search. */
export function isNotCandidateStage(stage: string): boolean {
  return NOT_CANDIDATES.has(stage);
}

/**
 * One decimal, ROUNDING HALF TO EVEN, to match the backend's `_pct`
 * (`packages/python-shared/nu_assist_shared/domain/search_report.py`, `_pct`)
 * and the export's `_pct_of`
 * (`packages/api/app/web/v2/search_report/domain_export.py`).
 *
 * NAMED, NOT LINE-NUMBERED, on purpose: the iter-1 and iter-2 reviews of
 * PR #797 each caught a line citation in this file that had already gone stale,
 * one of them in the comment added to fix the other.
 *
 * Mirrored rather than imported because the wire carries the result, not the
 * function. The first cut of this used `Math.round`, which rounds half AWAY
 * from zero, while Python's `round(x, 1)` rounds half to EVEN — so on a
 * 16-candidate search the screen said `6.3%` and the downloaded spreadsheet
 * said `6.2%` for the same stage. Off by a tenth of a point, on two documents
 * a reader has open side by side, and the reader blames the filter.
 *
 *   part/whole   Math.round   round-half-even (Python, and now this)
 *      1/16         6.3                6.2
 *      5/16        31.3               31.2
 *      9/16        56.3               56.2
 *
 * WHAT THIS DOES NOT PROMISE, and the mechanism, corrected after the iter-2
 * review of PR #797 measured it (B3.3). The earlier version of this note blamed
 * "both languages round the nearest double", which is wrong: the divergence is
 * introduced HERE, by the `* 10`.
 *
 * Python rounds the double as it stands at one decimal. This scales by ten
 * first — and that multiplication snaps a NEAR-tie onto an EXACT one. `1/2000`
 * is `0.05` in decimal but a hair above it as a double, so Python sees
 * "above the tie" and returns `0.1`; multiplying by ten lands on exactly `0.5`,
 * this reads a tie, rounds to even, and returns `0.0`.
 *
 * Measured over all 8,006,000 `(part, whole)` pairs with `whole <= 4000`:
 * **804 divergences, at exactly two denominators — `whole = 2000` and
 * `whole = 4000`, 402 each. Zero at every other denominator, including all
 * 180,900 pairs with `whole <= 600`.** (The `Math.round` this replaced, over
 * the same sweep: **3400** divergences, starting at `whole = 16` — so the fix
 * removes three quarters of them and pushes the first occurrence from a
 * 16-candidate search out to a 2000-candidate one.)
 *
 * So the residual is not "large denominators drift" — it is two specific
 * values, the ones where `part * 100 / whole` lands on an exact hundredths tie
 * that the double cannot hold. Both call sites feed INTEGER counts off the
 * wire, so no other input path exists to widen it.
 *
 * Independently brute-forced twice during the iter-2 review of PR #797, and the
 * alternatives were checked rather than assumed: scaling as `(part * 1000) /
 * whole` gives identical results, and exact-rational ties-to-even would be
 * worse, not better.
 *
 * `stageMirrorSync.test.ts` pins the tie cases against values computed in
 * Python, and separately asserts that Python's own helper still rounds the way
 * this claims it does.
 */
function pct(part: number, whole: number): number {
  if (whole <= 0) return 0;
  const scaled = ((part * 100) / whole) * 10;
  const lower = Math.floor(scaled);
  const remainder = scaled - lower;
  if (remainder > 0.5) return (lower + 1) / 10;
  if (remainder < 0.5) return lower / 10;
  // Exact tie: round to even, as Python does.
  return (lower % 2 === 0 ? lower : lower + 1) / 10;
}

/**
 * The donut's slices, with the not-a-candidate stages dropped.
 *
 * THE PERCENTAGES ARE RECOMPUTED, and skipping that step is the trap here. The
 * backend's `pct` is a share of every counted candidate; drop two stages and
 * keep those numbers and the drawn slices no longer sum to 100 while the ring
 * still closes — the reader sees a full circle whose labels add up to 91% and
 * concludes the chart lost something. Re-basing onto the charted population
 * keeps the widget internally honest.
 *
 * `total_candidacies` is NOT touched by this: the KPI above the chart stays a
 * factual count of the search and keeps reconciling with Thrive. What changes is
 * only what the donut divides by, which is why the panel's tooltip has to name
 * its denominator rather than leaving the reader to assume it is the KPI.
 */
export function candidateStageBuckets(
  buckets: readonly SearchReportBucket[]
): SearchReportBucket[] {
  const kept = buckets.filter((bucket) => !NOT_CANDIDATES.has(bucket.label));
  const total = kept.reduce((sum, bucket) => sum + bucket.count, 0);
  return kept.map((bucket) => ({ ...bucket, pct: pct(bucket.count, total) }));
}

/**
 * The funnel's rungs, with the not-a-candidate stages dropped.
 *
 * `pct_of_first` is a share of the WIDEST rung, and `Source` is rung 1 — so it
 * is normally the widest, and removing it silently invalidates every percentage
 * below it. The new first rung becomes the denominator.
 *
 * The series stays cumulative and monotone: dropping a rung from a "reached at
 * least this stage" ladder removes a row, it does not redistribute anyone. The
 * counts are already correct; only their basis moves.
 */
export function candidateFunnelSteps(
  steps: readonly SearchReportFunnelStep[]
): SearchReportFunnelStep[] {
  const kept = steps.filter((step) => !NOT_CANDIDATES.has(step.stage));
  const widest = kept[0]?.count ?? 0;
  return kept.map((step) => ({ ...step, pct_of_first: pct(step.count, widest) }));
}

/**
 * True for a stage series that should start deactivated.
 *
 * The predicate form is the one to reach for when a chart keeps its own hidden
 * state keyed by stage (`useSeriesToggles`), because it answers for a stage the
 * chart has not seen yet — a stage that only appears after the reader widens the
 * date range still arrives hidden, where a set snapshotted at mount would let it
 * in.
 */
export function stageHiddenByDefault(stage: string): boolean {
  // Two rules, and the order does not matter because they never disagree: off
  // the ladder hides it, and so does being a not-a-candidate stage. The second
  // only bites for `Source`, which is on the ladder — see the note on
  // {@link STAGES_NOT_CANDIDATES} for why that exception is intentional.
  return !isLadderStage(stage) || NOT_CANDIDATES.has(stage);
}

/**
 * The subset of `stages` that starts deactivated.
 *
 * The set form, for a widget that needs the answer for a known, fixed series
 * list in one call rather than one key at a time.
 */
export function defaultHiddenStages(stages: readonly string[]): ReadonlySet<string> {
  return new Set(stages.filter(stageHiddenByDefault));
}
